import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLiveCallsMonitoring } from "@/hooks/useCallAnalytics";
import { useEffect, useState } from "react";
import { AlertTriangle, Phone } from "lucide-react";

export const LiveCallsMonitor = () => {
  const { data: liveCalls } = useLiveCallsMonitoring();
  const [alertCalls, setAlertCalls] = useState<string[]>([]);

  useEffect(() => {
    // Monitor for negative sentiment calls that need supervisor alerts
    const negativeCalls = liveCalls?.filter(call => 
      call.sentiment_score !== null && call.sentiment_score < -0.5
    ).map(call => call.id) || [];
    
    setAlertCalls(negativeCalls);
  }, [liveCalls]);

  const formatDuration = (startedAt: string) => {
    const start = new Date(startedAt);
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - start.getTime()) / 1000);
    const minutes = Math.floor(diffSeconds / 60);
    const seconds = diffSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getSentimentBadge = (score: number | null) => {
    if (score === null) return <Badge variant="secondary">Analyzing...</Badge>;
    if (score > 0.3) return <Badge variant="default" className="bg-green-100 text-green-800">Positive</Badge>;
    if (score < -0.3) return <Badge variant="destructive">Negative</Badge>;
    return <Badge variant="secondary">Neutral</Badge>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Phone className="h-4 w-4" />
          Live Calls Monitor
        </CardTitle>
        {alertCalls.length > 0 && (
          <div className="flex items-center gap-1 text-red-600">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-xs">{alertCalls.length} Alert{alertCalls.length > 1 ? 's' : ''}</span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {liveCalls && liveCalls.length > 0 ? (
          <div className="space-y-3">
            {liveCalls.map(call => (
              <div 
                key={call.id} 
                className={`p-3 rounded-lg border ${
                  alertCalls.includes(call.id) ? 'border-red-200 bg-red-50' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="text-sm font-medium">
                    {call.from_number} → {call.to_number}
                  </div>
                  <Badge variant={call.status === 'ringing' ? 'secondary' : 'default'}>
                    {call.status}
                  </Badge>
                </div>
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>Duration: {formatDuration(call.started_at)}</span>
                  {getSentimentBadge(call.sentiment_score)}
                </div>
                {call.sentiment_score !== null && call.sentiment_score < -0.5 && (
                  <div className="mt-2 text-xs text-red-600 font-medium">
                    ⚠️ Supervisor attention needed - Negative sentiment detected
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No active calls</p>
        )}
      </CardContent>
    </Card>
  );
};